import "./ActiveWords.css";
import { useState, useEffect, useRef, useCallback } from "react"
import { ToastContainer, toast } from "react-toastify"
import { FaTrash  } from 'react-icons/fa'
import "react-toastify/dist/ReactToastify.css"
import { useGlobal } from '../GlobalContext'
// AgGrid:
// import { AgGridReact } from "ag-grid-react"
// import { AllCommunityModule, ModuleRegistry, themeQuartz } from 'ag-grid-community'
// ModuleRegistry.registerModules([AllCommunityModule])
// const myTheme = themeQuartz.withParams({
//   accentColor: "red",
//   fontFamily: 'Roboto',
//   fontSize: "12px",
//   headerFontFamily: 'Roboto',
//   headerFontSize: "12px",
//   headerFontWeight: "bold",
// })

// global vars:
var gTopic, gServer, gWordToDelete, gWordIndex = -1, gVdssSessionId, gSessionId, gWordId = ""
let wsInstance = null
const wssVersion = 5
let gAwords = []

export const getActiveWordsButtonLabel = (totalCount, filteredCount, hasFilter) => {
  if (!hasFilter || filteredCount === totalCount) {
    return `Active (${totalCount})`
  }

  return `Active (${filteredCount} / ${totalCount})`
}

export const getActiveWordsColumnCount = (containerWidth) => {
  if (!Number.isFinite(containerWidth) || containerWidth <= 0) {
    return 3
  }

  // Doubled thresholds: previously 200 / 400 -> now 400 / 800
  if (containerWidth < 400) {
    return 1
  }

  if (containerWidth < 800) {
    return 2
  }

  return 3
}

export const ACTIVE_WORDS_VIEWPORT_SIZE = 200
export const ACTIVE_WORDS_GRID_GAP = 10
export const ACTIVE_WORDS_DEFAULT_ROW_HEIGHT = 52

/**
 * How many word cards fit on one screen (one "page") for the current layout.
 * Used by PageUp/PageDown — recalculate on resize.
 */
export const getActiveWordsVisiblePageSize = ({
  containerHeight,
  columnCount,
  rowHeight = ACTIVE_WORDS_DEFAULT_ROW_HEIGHT,
  gap = ACTIVE_WORDS_GRID_GAP,
  paddingY = 0,
} = {}) => {
  const cols = Math.max(1, Math.floor(Number(columnCount)) || 1)
  const usableHeight = Math.max(0, Number(containerHeight) || 0) - Math.max(0, Number(paddingY) || 0)
  if (usableHeight <= 0) {
    return cols
  }
  const pitch = Math.max(1, (Number(rowHeight) || ACTIVE_WORDS_DEFAULT_ROW_HEIGHT) + Math.max(0, gap))
  const rows = Math.max(1, Math.floor((usableHeight + Math.max(0, gap)) / pitch))
  return rows * cols
}

export const clampScrollPercent = (percent) =>
  Math.min(100, Math.max(0, percent))

/** Max start index for a virtual window of `viewportSize` over `totalCount` words. */
export const getActiveWordsMaxStartIndex = (totalCount, viewportSize = ACTIVE_WORDS_VIEWPORT_SIZE) =>
  Math.max(0, totalCount - viewportSize)

export const getActiveWordsStartIndex = (
  scrollPercent,
  totalCount,
  viewportSize = ACTIVE_WORDS_VIEWPORT_SIZE
) => {
  const maxStart = getActiveWordsMaxStartIndex(totalCount, viewportSize)
  if (maxStart === 0) return 0
  return Math.min(
    maxStart,
    Math.max(0, Math.round((clampScrollPercent(scrollPercent) / 100) * maxStart))
  )
}

export const scrollPercentFromStartIndex = (
  startIndex,
  totalCount,
  viewportSize = ACTIVE_WORDS_VIEWPORT_SIZE
) => {
  const maxStart = getActiveWordsMaxStartIndex(totalCount, viewportSize)
  if (maxStart === 0) return 0
  return clampScrollPercent((Math.min(maxStart, Math.max(0, startIndex)) / maxStart) * 100)
}

/** Step the virtual window by `stepSize` words (default: one full viewport / page). */
export const pageActiveWordsScrollPercent = (
  scrollPercent,
  totalCount,
  direction,
  viewportSize = ACTIVE_WORDS_VIEWPORT_SIZE,
  stepSize = viewportSize
) => {
  const maxStart = getActiveWordsMaxStartIndex(totalCount, viewportSize)
  if (maxStart === 0) return 0
  const currentStart = getActiveWordsStartIndex(scrollPercent, totalCount, viewportSize)
  const delta = (direction < 0 ? -1 : 1) * Math.max(1, stepSize)
  return scrollPercentFromStartIndex(currentStart + delta, totalCount, viewportSize)
}

/** Map pointer Y on the scrubber track to scroll percent (fine, linear). */
export const scrollPercentFromTrackPointer = (clientY, trackRect, thumbSize) => {
  if (!trackRect || trackRect.height <= 0) return 0
  const usable = Math.max(1, trackRect.height - thumbSize)
  const y = clientY - trackRect.top - thumbSize / 2
  return clampScrollPercent((y / usable) * 100)
}

/** Classic Page Up/Down keycap metaphor: stop-bar + chevron (like ⇞ / ⇟). */
const ScrubberPageIcon = ({ direction }) => {
  const isUp = direction < 0
  return (
    <svg
      className="aw-scrubber__icon"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      {isUp ? (
        <>
          <path d="M3.5 3.25h9" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
          <path
            d="M8 13.25V6.4M8 6.4 4.85 9.55M8 6.4l3.15 3.15"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      ) : (
        <>
          <path
            d="M8 2.75v6.85M8 9.6 4.85 6.45M8 9.6l3.15-3.15"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M3.5 12.75h9" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
        </>
      )}
    </svg>
  )
}

const ScrubberRowIcon = ({ direction }) => (
  <svg
    className="aw-scrubber__icon"
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden="true"
  >
    {direction < 0 ? (
      <path
        d="M6 9.25V3.4M6 3.4 3.2 6.2M6 3.4l2.8 2.8"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ) : (
      <path
        d="M6 2.75v5.85M6 8.6 3.2 5.8M6 8.6l2.8-2.8"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    )}
  </svg>
)

// Setter functions for global variables
export const setGlobalTopic = (topic) => {
  gTopic = topic
  gServer = "WSS://" + topic.url + ":" + topic.port
  gAwords = [] // Clear cached words on topic change
  console.log("setGlobalTopic", gAwords.length)
}

export const setGlobalSessionId = (sessionId) => {
  gSessionId = sessionId
}

export const setGlobalVdssSessionId = (vdssSessionId, setGvdssSessionId) => {
  gVdssSessionId = vdssSessionId
  init.config.session_id = vdssSessionId
  if (setGvdssSessionId) {
    setGvdssSessionId(vdssSessionId)
  }
}
var init = {
  config: {
    sample_rate: "16000",
    key: "2Njw!M2zApeAtdA6dG9kvBpzjT*rwqDbE2zzAs%P5Zuc?xzbpj!ZNnNA#u!gHM?%H4447TvPUgNtvGM?hgWe$qq#P2",
    session_id: gVdssSessionId, //"aaaa",
    version: wssVersion,
    loginSessionID: "",
    topicID: ""
  }
}

const WsGetAllWords = ({ sessionId, topic, searchQuery, reloadTrigger, onCountsChange, setShowTranscriptions, setShowAddWord, setSelectedWordData, selectionTokenRef, focusNonce = 0 }) => {
  const { gVdssSessionId: globalGvdssSessionId, setGvdssSessionId } = useGlobal()
  
  gSessionId = sessionId
  gTopic = topic
  gVdssSessionId = globalGvdssSessionId || gVdssSessionId
  gServer = "WSS://" + gTopic.url + ":" + gTopic.port
  //console.log("itt 3",gVdssSessionId,gTopic, gServer)
  init.config.session_id = gVdssSessionId
  init.config.loginSessionID = gSessionId
  init.config.topicID = gTopic.topic_id
  //console.log("WsGetAllWords init",init)
  const [Awords, setAwords] = useState([])
  var AwordsRef = useRef([])
  const [loading, setLoading] = useState(true)
  //const [reloadTrigger, setReloadTrigger] = useState(0)
  // eslint-disable-next-line no-unused-vars
	// const [gridApi, setGridApi] = useState(null)  // gridApi elmentése
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isAddingWord, setIsAddingWord] = useState(false)
  
  // Card view states
  const [visibleWords, setVisibleWords] = useState([])
  const [filteredWords, setFilteredWords] = useState([])
  const [displayCount, setDisplayCount] = useState(200)
  const [scrollPercent, setScrollPercent] = useState(0)
  const containerRef = useRef(null)
  const listLayoutRef = useRef(null)
  const scrubberTrackRef = useRef(null)
  const scrubberDraggingRef = useRef(false)
  const [internalReloadTrigger, setInternalReloadTrigger] = useState(0)
  const [columnCount, setColumnCount] = useState(3)
  const [pageSize, setPageSize] = useState(ACTIVE_WORDS_VIEWPORT_SIZE)
  const [windowSize, setWindowSize] = useState(ACTIVE_WORDS_VIEWPORT_SIZE)
  const filteredLengthRef = useRef(0)
  const SCRUBBER_THUMB_SIZE = 18

  filteredLengthRef.current = filteredWords.length

  // Apply search filter when searchQuery changes
  useEffect(() => {
    if (searchQuery && searchQuery.trim() !== "") {
      const query = searchQuery.trim().toLowerCase()
      const filtered = Awords.filter(word => {
        const wordLower = word.word.toLowerCase()
        // Match if query is at the start, contains, or matches exactly
        //return wordLower.startsWith(query) || wordLower.includes(query) || wordLower === query
        return wordLower.startsWith(query) || wordLower === query
      })
      setFilteredWords(filtered)
      setDisplayCount(200)
      setScrollPercent(0)
    } else {
      setFilteredWords(Awords)
      setDisplayCount(200)
      setScrollPercent(0)
    }
  }, [searchQuery, Awords])

  // Update visible words based on measured window size and scroll position
  useEffect(() => {
    if (filteredWords.length === 0) {
      setVisibleWords([])
      return
    }

    const startIndex = getActiveWordsStartIndex(scrollPercent, filteredWords.length, windowSize)
    const endIndex = Math.min(filteredWords.length, startIndex + windowSize)
    const sliced = filteredWords.slice(startIndex, endIndex)

    setVisibleWords(sliced)
  }, [filteredWords, scrollPercent, windowSize])

  useEffect(() => {
    if (onCountsChange) {
      const trimmedQuery = searchQuery?.trim() ?? ""
      const hasFilter = trimmedQuery !== ""
      const totalCount = Awords.length
      const filteredCount = hasFilter ? filteredWords.length : totalCount

      onCountsChange({
        totalCount,
        filteredCount,
        hasFilter,
      })
    }
  }, [Awords.length, filteredWords.length, onCountsChange, searchQuery])

  const measureListLayout = useCallback(() => {
    const el = containerRef.current
    if (!el) return

    const rect = el.getBoundingClientRect()
    const cols = getActiveWordsColumnCount(rect.width)
    setColumnCount(cols)

    const style = window.getComputedStyle(el)
    const paddingY =
      (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0)
    const card = el.querySelector(".word-card")
    const rowHeight = card
      ? card.getBoundingClientRect().height
      : ACTIVE_WORDS_DEFAULT_ROW_HEIGHT

    const nextPageSize = getActiveWordsVisiblePageSize({
      containerHeight: rect.height,
      columnCount: cols,
      rowHeight,
      gap: ACTIVE_WORDS_GRID_GAP,
      paddingY,
    })
    // Render exactly one on-screen page so PgUp/PgDn matches what you see
    const nextWindowSize = Math.max(cols, nextPageSize)

    setPageSize((prev) => (prev === nextPageSize ? prev : nextPageSize))
    setWindowSize((prevWindow) => {
      if (prevWindow === nextWindowSize) return prevWindow
      const total = filteredLengthRef.current
      setScrollPercent((prevPercent) => {
        const start = getActiveWordsStartIndex(prevPercent, total, prevWindow)
        return scrollPercentFromStartIndex(start, total, nextWindowSize)
      })
      return nextWindowSize
    })

    el.scrollTop = 0
  }, [])

  useEffect(() => {
    measureListLayout()
    const el = containerRef.current
    if (!el || typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measureListLayout)
      return () => window.removeEventListener("resize", measureListLayout)
    }

    const ro = new ResizeObserver(() => {
      measureListLayout()
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [measureListLayout])

  // Remeasure once cards exist (row height) / slice length changes
  useEffect(() => {
    measureListLayout()
  }, [visibleWords.length, measureListLayout])

  const applyScrubberPointer = useCallback((clientY) => {
    const track = scrubberTrackRef.current
    if (!track) return
    const nextPercent = scrollPercentFromTrackPointer(
      clientY,
      track.getBoundingClientRect(),
      SCRUBBER_THUMB_SIZE
    )
    setScrollPercent(nextPercent)
    if (containerRef.current) containerRef.current.scrollTop = 0
  }, [SCRUBBER_THUMB_SIZE])

  const handleScrubberPointerDown = useCallback((e) => {
    e.preventDefault()
    scrubberDraggingRef.current = true
    applyScrubberPointer(e.clientY)

    const onMove = (ev) => {
      if (!scrubberDraggingRef.current) return
      applyScrubberPointer(ev.clientY)
    }
    const onUp = () => {
      scrubberDraggingRef.current = false
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }, [applyScrubberPointer])

  const handleRowScroll = useCallback((direction) => {
    setScrollPercent((prev) =>
      pageActiveWordsScrollPercent(
        prev,
        filteredWords.length,
        direction,
        windowSize,
        columnCount
      )
    )
    if (containerRef.current) containerRef.current.scrollTop = 0
  }, [filteredWords.length, columnCount, windowSize])

  const handleViewportScroll = useCallback((direction) => {
    setScrollPercent((prev) =>
      pageActiveWordsScrollPercent(
        prev,
        filteredWords.length,
        direction,
        windowSize,
        pageSize
      )
    )
    if (containerRef.current) containerRef.current.scrollTop = 0
  }, [filteredWords.length, windowSize, pageSize])

  const handleListKeyDown = useCallback((e) => {
    if (filteredWords.length === 0) return

    switch (e.key) {
      case "ArrowUp":
        e.preventDefault()
        handleRowScroll(-1)
        break
      case "ArrowDown":
        e.preventDefault()
        handleRowScroll(1)
        break
      case "PageUp":
        e.preventDefault()
        handleViewportScroll(-1)
        break
      case "PageDown":
        e.preventDefault()
        handleViewportScroll(1)
        break
      default:
        break
    }
  }, [filteredWords.length, handleRowScroll, handleViewportScroll])

  const focusWordList = useCallback(() => {
    listLayoutRef.current?.focus()
  }, [])

  // Autofocus when entering Active tab / remounting, and when parent bumps focusNonce
  useEffect(() => {
    const id = window.setTimeout(() => {
      focusWordList()
    }, 0)
    return () => window.clearTimeout(id)
  }, [focusNonce, focusWordList])

  // Reset display count when data changes
  useEffect(() => {
    setDisplayCount(200)
    setScrollPercent(0)
  }, [Awords, reloadTrigger, internalReloadTrigger])

  const [newWordText, setNewWordText] = useState("")
  const [soundsLikeText, setSoundsLikeText] = useState("")

  useEffect(() => {
    if (wsInstance) {
      console.log("WebSocket már fut, nem indítjuk újra.")
      return
    }
    
    // Check if we have cached words
    if (gAwords.length > 0) {
      console.log("Using cached words, gAwords length:", gAwords.length)
      setAwords(gAwords)
      AwordsRef.current = gAwords
      setLoading(false)
      return
    } else {
      console.log("No cached words, loading from WebSocket")
    }
    setLoading(true)
    setAwords([])
    AwordsRef.current = []
    const WSS = new WebSocket(gServer)
    WSS.onopen = function () {
      var kaptamSessiont = false
      console.log("GetAllWords előtti init",init)
      try {
        WSS.send(JSON.stringify(init))
        var command = JSON.stringify({
          command: "GetAllWords",
          key: "ZNnNA#u!gHM?%H4447TvPUgNtvGM?hgWe$qq#P2",
          sessionId: gVdssSessionId,
          topicId: gTopic.topic_id,
        })
        console.log("GetAllWords command",command)
        WSS.send(command)

        WSS.onmessage = function (event) {
          if (!kaptamSessiont) {
            var data = JSON.parse(event.data)
            //console.log(typeof data, data)
            gVdssSessionId = data.results.sessionID
            console.log("gVdssSessionId from WebSocket", gVdssSessionId)
            init.config.session_id = gVdssSessionId
            kaptamSessiont = true
          } else {
            console.log("Received GetAllWords message:", event)
            if (event.data !== "__END__") {
              try {
                var data = JSON.parse(event.data)
                if (data && data.words && Array.isArray(data.words)) {
                  for (var n = 0; n < data.words.length; n++) {
                    const newWord = {
                      word: data.words[n].trim(),
                      index: n,
                      delete: "delete",
                      visibility: "1",
                    }
                    AwordsRef.current.push(newWord)
                  }
                  setAwords(AwordsRef.current)
                  // Store in global cache
                  gAwords = [...AwordsRef.current]
                  console.log("Cached words, gAwords length:", gAwords.length)
                }
              } catch (error) {
                console.error("Error parsing WebSocket message:", error, event.data)
              }
              setLoading(false)
            }
            WSS.close()
          }
        }
      } catch (error) {
        console.error("WsGetAllWords WSS send error", error)
      }
    }
    WSS.onerror = (error) => {
      console.error("WsGetAllWords WebSocket error:", error)
      //setLoading(false)
    }
    WSS.onclose = () => {
      console.log("WsGetAllWords WSS Connection closed")
      setLoading(false)
    }
  //}, [gTopic, gSessionId, gVdssSessionId])
  }, [topic, reloadTrigger])

  const handleShowConfirm = (word, setShowConfirmModal, index) => {
    gWordToDelete = word
    gWordIndex = index
    console.log("gWordToDelete",gWordToDelete, gWordIndex)
    setShowConfirmModal(true)
  }

  const handleDelete = async (setShowConfirmModal) => {
    setIsDeleting(true)

    // Optimistically remove the word from local state and cache so the card list updates immediately
    try {
      setAwords(prev => {
        const updated = prev.filter(w => !(w.word === gWordToDelete && w.index === gWordIndex))
        AwordsRef.current = updated
        gAwords = [...updated]
        return updated
      })
      setFilteredWords(prev => prev.filter(w => !(w.word === gWordToDelete && w.index === gWordIndex)))

      const wordId = await wsGetWordInfo()
      console.log("wordId to delete",wordId)
      wsHandleDelete(setShowConfirmModal, setInternalReloadTrigger, setIsDeleting)
    } catch (error) {
      console.log("Error in handleDelete, will trigger full reload", error)
      // On error, clear cache and trigger reload to restore consistent state
      setIsDeleting(false)
      setShowConfirmModal(false)
      gAwords = []
      setInternalReloadTrigger(prev => prev + 1)
    }
  }
  
  const handleSaveWord = (setShowAddWordModal) => {
    console.log(newWordText,soundsLikeText)
    if ( newWordText ) {
      setIsAddingWord(true)
      setShowAddWordModal(false)
      if ( soundsLikeText === "" ) {
        wsGenerateTranscriptions("newWord",newWordText,soundsLikeText,setNewWordText,setSoundsLikeText,setIsAddingWord,setInternalReloadTrigger)
      } else {
        wsGenerateTranscriptions("soundsLike",newWordText,soundsLikeText,setNewWordText,setSoundsLikeText,setIsAddingWord,setInternalReloadTrigger)
      }
    }
  }


  const handleGetWordInfo = async (word) => {
    gWordToDelete = word
    console.log("handleGetWordInfo", gWordToDelete)
    if (selectionTokenRef) selectionTokenRef.current += 1
    const myToken = selectionTokenRef ? selectionTokenRef.current : null
    const wordData = await wsGetWordInfo()
    console.log("wordData from wsGetWordInfo:", wordData)
    if (selectionTokenRef && selectionTokenRef.current !== myToken) {
      console.log("handleGetWordInfo: stale response, a newer word was selected meanwhile — ignoring")
      return
    }
    if (setSelectedWordData) {
      setSelectedWordData({
        word: word,
        word_id: wordData.word_id,
        transcriptions: wordData.transcriptions || []
      })
    }
    if (setShowTranscriptions) {
      setShowTranscriptions(true)
    }
    if (setShowAddWord) {
      setShowAddWord(false)
    }
  }

  /*
  ██   ██ ████████ ███    ███ ██      
  ██   ██    ██    ████  ████ ██      
  ███████    ██    ██ ████ ██ ██      
  ██   ██    ██    ██  ██  ██ ██      
  ██   ██    ██    ██      ██ ███████ 
  */

  return (

    <>
      <div
        className="aw-list-layout"
        ref={listLayoutRef}
        tabIndex={0}
        role="region"
        aria-label="Active words list"
        onKeyDown={handleListKeyDown}
        onMouseDown={(e) => {
          // Focus the list for keyboard nav, but let scrubber buttons keep their click
          if (e.target.closest("button, input, textarea, a, [contenteditable='true']")) return
          focusWordList()
        }}
      >
        <div 
          className="topic-words-container card-view-container"
          ref={containerRef}
          style={{ display: 'grid', gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`, gap: '10px', overflowY: 'hidden', height: '100%', maxHeight: 'none', padding: '10px', alignContent: 'start' }}
        >
          {visibleWords.map((wordItem, index) => {
            return (
            <div 
              key={`${wordItem.word}-${wordItem.index}`} 
              className="word-card"
              onClick={() => {
                console.log("Kártyára kattintott", wordItem)
                handleGetWordInfo(wordItem.word)
              }}
                style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', gap: '8px', cursor: 'default' }}
              onMouseEnter={(e) => {
                const button = e.currentTarget.querySelector('button')
                if (button) {
                  button.style.opacity = '1'
                  button.style.pointerEvents = 'auto'
                }
              }}
              onMouseLeave={(e) => {
                const button = e.currentTarget.querySelector('button')
                if (button) {
                  button.style.opacity = '0'
                  button.style.pointerEvents = 'none'
                }
              }}
            >
              <span style={{ fontSize: '14px', fontWeight: '500', flex: 1, wordBreak: 'break-word', overflowWrap: 'break-word', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{wordItem.word}</span>
              <button 
                className="grid-button-transparent"
                onClick={(e) => {
                  e.stopPropagation()
                  handleShowConfirm( wordItem.word, setShowConfirmModal, wordItem.index)
                }}              
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', opacity: 0, pointerEvents: 'none', transition: 'opacity 0.2s ease' }}
              >
                <FaTrash className="icon-red" style={{ color: '#dc2626' }} />
              </button>
            </div>
            )
          })}
          {visibleWords.length === 0 && !loading && (
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px', color: '#666' }}>
              No documents
            </div>
          )}
        </div>

        <div
          className="aw-scrubber"
          aria-label="Active words position"
        >
          <button
            type="button"
            className="aw-scrubber__btn aw-scrubber__btn--page"
            aria-label="Page up"
            title="Page up"
            tabIndex={-1}
            disabled={filteredWords.length <= windowSize || scrollPercent <= 0}
            onClick={() => {
              handleViewportScroll(-1)
              focusWordList()
            }}
          >
            <ScrubberPageIcon direction={-1} />
          </button>
          <button
            type="button"
            className="aw-scrubber__btn"
            aria-label="Previous row"
            title="Previous row"
            tabIndex={-1}
            disabled={filteredWords.length <= windowSize || scrollPercent <= 0}
            onClick={() => {
              handleRowScroll(-1)
              focusWordList()
            }}
          >
            <ScrubberRowIcon direction={-1} />
          </button>
          <div
            className="aw-scrubber__track"
            ref={scrubberTrackRef}
            onPointerDown={(e) => {
              handleScrubberPointerDown(e)
              focusWordList()
            }}
          >
            <div
              className="aw-scrubber__thumb"
              style={{ height: SCRUBBER_THUMB_SIZE, width: SCRUBBER_THUMB_SIZE, top: `calc((100% - ${SCRUBBER_THUMB_SIZE}px) * ${scrollPercent / 100})` }}
              role="slider"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(scrollPercent)}
              aria-label="Scroll position"
            />
          </div>
          <button
            type="button"
            className="aw-scrubber__btn"
            aria-label="Next row"
            title="Next row"
            tabIndex={-1}
            disabled={filteredWords.length <= windowSize || scrollPercent >= 100}
            onClick={() => {
              handleRowScroll(1)
              focusWordList()
            }}
          >
            <ScrubberRowIcon direction={1} />
          </button>
          <button
            type="button"
            className="aw-scrubber__btn aw-scrubber__btn--page"
            aria-label="Page down"
            title="Page down"
            tabIndex={-1}
            disabled={filteredWords.length <= windowSize || scrollPercent >= 100}
            onClick={() => {
              handleViewportScroll(1)
              focusWordList()
            }}
          >
            <ScrubberPageIcon direction={1} />
          </button>
        </div>
      </div>

      {loading && 
        <div className="flex-column">
          <div className="modal-overlay">
              <div className="loading-indicator">Loading...</div>
              <div className="spinner"></div>
          </div>
        </div>
      }
      {/* Törlés megerősítő modal */}
      { showConfirmModal && !isDeleting && (
        <div className="modal-overlay">
          <div className="modal-content">
            <p>Are you sure you want to delete <strong>{gWordToDelete}</strong>?</p>
            <div className="modal-buttons">
              <button
                onClick={() => {
                  handleDelete(setShowConfirmModal)
                  //setShowConfirmModal(false)
                }}
                className="confirm-button"
              >
                Delete
              </button>
              <button
                onClick={() => setShowConfirmModal(false)}
                className="cancel-button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deleting loading modal */}
      {isDeleting && (
        <div className="modal-overlay" style={{ zIndex: 9999 }}>
          <div className="loading-indicator">Deleting word...</div>
          <div className="spinner"></div>
        </div>
      )}

      {/* Adding word loading modal */}
      {isAddingWord && (
        <div className="modal-overlay" style={{ zIndex: 9999 }}>
          <div className="loading-indicator">Adding word...</div>
          <div className="spinner"></div>
        </div>
      )}

      {/* Add Word modal

      { showAddWordModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <p>Add new word-----</p>
            <input
              type="text"
              placeholder="New word..."
              className="w-full p-2 mb-4 border border-blue-300 rounded"
              value={newWordText}
              onChange={(e) => setNewWordText(e.target.value)}
              style={{ margin: "10px auto", padding: "5px", width: "90%",alignSelf:"center" }}
            />
            <input
              type="text"
              placeholder="Sounds like..."
              className="w-full p-2 mb-4 border border-blue-300 rounded"
              value={soundsLikeText}
              onChange={(e) => setSoundsLikeText(e.target.value)}
              style={{ margin: "10px auto", padding: "5px", width: "90%",alignSelf:"center" }}
            />
            <div className="modal-buttons">
              <button
                onClick={() => {
                  handleSaveWord(setShowAddWordModal)
                  //setShowConfirmModal(false)
                }}
                className="cancel-button"
              >
                Save
              </button>
              <button
                onClick={() => setShowAddWordModal(false)}
                className="cancel-button"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      */}

      <ToastContainer   
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        rtl={false}
        style={{ zIndex: 9999, position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
        />
    </>
  )
}
export default WsGetAllWords


/*
██     ██ ███████     ██████  ███████ ██      ███████ ████████ ███████ 
██     ██ ██          ██   ██ ██      ██      ██         ██    ██      
██  █  ██ ███████     ██   ██ █████   ██      █████      ██    █████   
██ ███ ██      ██     ██   ██ ██      ██      ██         ██    ██      
 ███ ███  ███████     ██████  ███████ ███████ ███████    ██    ███████ 
*/


export const wsHandleDelete = (setShowConfirmModal, setReloadTrigger, setIsDeleting) => {
  console.log("Deleting word:",gWordToDelete, "sessionId",gSessionId,"topic_id",gTopic.topic_id,"vdssSessionId",gVdssSessionId)
  const WSS = new WebSocket(gServer)
  WSS.onopen = function () {
    var kaptamSessiont = false
    console.log("wsHandleDelete init",init)
    try {
      WSS.send(JSON.stringify(init))
      var command = JSON.stringify({
        command: "DeleteWord",
        sessionId : gSessionId,
        wordId : gWordId,
        topicId: gTopic.topic_id,
      })
      WSS.send(command)
      WSS.onmessage = function (event) {
        if (!kaptamSessiont) {
          gVdssSessionId = event.data
          console.log("gVdssSessionId IN wsHandleDelete",gVdssSessionId)
          kaptamSessiont = true
        } else {
          console.log(event.data)
          setShowConfirmModal(false)
          WSS.close()
          gAwords = []
          if (setReloadTrigger) {
            setReloadTrigger(prev => prev + 1)
          }
          if (setIsDeleting) {
            setIsDeleting(false)
          }
        }
      }
    } catch (error) { console.error("WsGetAllWords WSS send error", error) }
  }
  WSS.onerror = (error) => {
    console.error("WsGetAllWords WebSocket error:", error)
    if (setIsDeleting) {
      setIsDeleting(false)
    }
  }
  WSS.onclose = () => {
    console.log("WsGetAllWords WSS Connection closed")
    if (setIsDeleting) {
      setIsDeleting(false)
    }
  }
  return () => {
    WSS.close()
    if (setIsDeleting) {
      setIsDeleting(false)
    }
  }
}

/*
██     ██ ███████     ██     ██  ██████  ██████  ██████      ██ ███    ██ ███████  ██████  
██     ██ ██          ██     ██ ██    ██ ██   ██ ██   ██     ██ ████   ██ ██      ██    ██ 
██  █  ██ ███████     ██  █  ██ ██    ██ ██████  ██   ██     ██ ██ ██  ██ █████   ██    ██ 
██ ███ ██      ██     ██ ███ ██ ██    ██ ██   ██ ██   ██     ██ ██  ██ ██ ██      ██    ██ 
 ███ ███  ███████      ███ ███   ██████  ██   ██ ██████      ██ ██   ████ ██       ██████  
*/

export const wsGetWordInfo = async (setShowConfirmModal = null) => {
  console.log("wsGetWordInfo gWordToDelete:",gWordToDelete)
  return new Promise((resolve, reject) => {
    const WSS = new WebSocket(gServer)
    WSS.onopen = function () {
      var kaptamSessiont = false
      try {
        WSS.send(JSON.stringify(init))
        var command = JSON.stringify({
          command: "GetWordInfo",
          word : gWordToDelete,
        })
        WSS.send(command)
        WSS.onmessage = function (event) {
          if (!kaptamSessiont) {
            gVdssSessionId = event.data
            console.log("gVdssSessionId IN wsGetWordInfo",gVdssSessionId)
            kaptamSessiont = true
          } else {
            var data = JSON.parse(event.data)
            if ( data.success ) {
              console.log("gWordId",data.results)
              gWordId = data.results.word_id
              console.log("gWordId",data.results.word_id)
              resolve(data.results)
            }
            WSS.close()
          }
        }
      } catch (error) {
        console.error("wsGetWordInfo WSS send error", error)
      }
    }
    WSS.onerror = (error) => {
      console.error("wsGetWordInfo WebSocket error:", error)
      reject(false)
    }
    WSS.onclose = () => {
      console.log("wsGetWordInfo WSS Connection closed")
      reject(false)
    }
    return () => {
      WSS.close()
    }
  })  // PROMISE vége
}


/*
██     ██ ███████      ██████  ███████ ███    ██     ████████ ██████   █████  ███    ██ ███████ 
██     ██ ██          ██       ██      ████   ██        ██    ██   ██ ██   ██ ████   ██ ██      
██  █  ██ ███████     ██   ███ █████   ██ ██  ██        ██    ██████  ███████ ██ ██  ██ ███████ 
██ ███ ██      ██     ██    ██ ██      ██  ██ ██        ██    ██   ██ ██   ██ ██  ██ ██      ██ 
 ███ ███  ███████      ██████  ███████ ██   ████        ██    ██   ██ ██   ██ ██   ████ ███████ 
*/

export const wsGenerateTranscriptions = (mode, word, soundsLike, setNewWordText, setSoundsLikeText, setIsAddingWord, setReloadTrigger) => {
  console.log("wsGenerateTranscriptions mode, word, soundsLike:", mode, word, soundsLike)
    const WSS = new WebSocket(gServer)
    WSS.onopen = function () {
      var kaptamSessiont = false
      //console.log("wsGenerateTranscriptions init",init)
      try {
        WSS.send(JSON.stringify(init))
        var command
        if ( mode === "newWord") {
          command = "GenerateTranscriptions:" + word
        } else {
          command = "GenerateTranscriptions:" + soundsLike
        }
        WSS.send(command)
        console.log("command", command)
        WSS.onmessage = function (event) {
          if (!kaptamSessiont) {
            gVdssSessionId = event.data
            console.log("gVdssSessionId IN wsGenerateTranscriptions",gVdssSessionId)
            kaptamSessiont = true
          } else {
            console.info("wsGenerateTranscriptions event",event.data)
            WSS.close()
            // ADD NEW WORD hívása
            wsAddNewWord(word, soundsLike, event.data, setNewWordText, setSoundsLikeText, setIsAddingWord, setReloadTrigger)
          }
        }
      } catch (error) {
        console.error("wsGenerateTranscriptions WSS send error", error)
        if (setIsAddingWord) {
          setIsAddingWord(false)
        }
      }
    }
  WSS.onerror = (error) => {
    console.error("wsGenerateTranscriptions WebSocket error:", error)
    if (setIsAddingWord) {
      setIsAddingWord(false)
    }
  }
  WSS.onclose = () => {
    console.log("wsGenerateTranscriptions WSS Connection closed")
    if (setIsAddingWord) {
      setIsAddingWord(false)
    }
  }
  return () => {
    WSS.close()
    /*if (setIsAddingWord) {
      setIsAddingWord(false)
    }*/
  }
}

/*
 █████  ██████  ██████      ███    ██ ███████ ██     ██     ██     ██  ██████  ██████  ██████  
██   ██ ██   ██ ██   ██     ████   ██ ██      ██     ██     ██     ██ ██    ██ ██   ██ ██   ██ 
███████ ██   ██ ██   ██     ██ ██  ██ █████   ██  █  ██     ██  █  ██ ██    ██ ██████  ██   ██ 
██   ██ ██   ██ ██   ██     ██  ██ ██ ██      ██ ███ ██     ██ ███ ██ ██    ██ ██   ██ ██   ██ 
██   ██ ██████  ██████      ██   ████ ███████  ███ ███       ███ ███   ██████  ██   ██ ██████  
*/

export const wsAddNewWord = (word, soundsLike, transcriptions, setNewWordText, setSoundsLikeText, setIsAddingWord, setReloadTrigger) => {
  console.log("wsAddNewWord word, soundsLike, transcriptions:", word, soundsLike, transcriptions)
    const WSS = new WebSocket(gServer)
    WSS.onopen = function () {
      var kaptamSessiont = false
      //console.log("wsAddNewWord init",init)
      try {
        WSS.send(JSON.stringify(init))
        var command = JSON.stringify({
          command: "AddWord",
          sessionId : gSessionId,
          word : word,
          transcriptions : transcriptions.split("%"),
          topicId : gTopic.topic_id,
        })
        console.info("wsSaveNewWord command", command)
        WSS.send(command)
        WSS.onmessage = function (event) {
          if (!kaptamSessiont) {
            gVdssSessionId = event.data
            console.log("gVdssSessionId IN wsAddNewWord",gVdssSessionId)
            kaptamSessiont = true
          } else {
            var data = JSON.parse(event.data)
            console.log("data", data)

            if ( data.success ) {
              console.log("Word added*************")
/*              toast.success("Word added", {
                position: "top-center",
                autoClose: 2000,
                style: { backgroundColor: "#ebf8ff", fontSize: "16px" },
              })*/
              setNewWordText("")
              setSoundsLikeText("")
              // Clear cache to force reload
              gAwords = []
              // Close modal after toast disappears (2000ms)
//              setTimeout(() => {
                if (setIsAddingWord) {
                  setIsAddingWord(false)
                }
                // Trigger grid reload
                if (setReloadTrigger) {
                  setReloadTrigger(prev => prev + 1)
                }
//              }, 0)
            } else {
              console.error(data.error)
              setTimeout(() => {
                if (setIsAddingWord) {
                  setIsAddingWord(false)
                }

                toast.error(data.error, {
                  position: "top-center",
                  autoClose: 0,
                  style: { backgroundColor: "#ebf8ff", fontSize: "16px" },
                })

              }, 5000)
            }
            WSS.close()
          }
        }
      } catch (error) {
        console.error("wsAddNewWord WSS send error", error)
        if (setIsAddingWord) {
          setIsAddingWord(false)
        }
      }
    }
    WSS.onerror = (error) => {
      console.error("wsAddNewWord WebSocket error:", error)
      if (setIsAddingWord) {
        setIsAddingWord(false)
      }
    }
    WSS.onclose = () => {
      console.log("wsAddNewWord WSS Connection closed")
    }
    return () => {
      WSS.close()
    }
}
